import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { Session } from "../../model_gen/session";
import { Application, Config } from "../../model_gen/application";
import { ApplicationService } from "../../services/ApplicationService";
import { SessionService } from "../../services/SessionService";
import { WorkflowService, WorkflowRun } from "../../services/WorkflowService";
import { calculateScore, getSessionScoreColor } from "../Session/SessionUtils";
import { AnalyticsService, ApplicationAnalyticsResponse, FaultRateAnalyticsResponse, RequestAnalyticsData, FaultRateAnalyticsData } from "../../services/AnalyticsService";

import SessionOverview from "../Session/SessionOverview";
import { EvaluateResult } from "../../model_gen/chat";
import { ApplicationAction } from "../../model_gen/application";
import { Model } from "../../model_gen/model";
import { Agent } from "../../model_gen/agent_base";
import { ModelService } from "../../services/ModelService";
import { AgentService } from "../../services/AgentService";
import ConfigurationPanel from "../configuration/ConfigurationPanel";
import { ConfigurationPanelRef } from "../../types/IApplication";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../ui/table";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { 
  ArrowUpDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  EyeIcon,
  SettingsIcon,
  AppWindowIcon,
  HistoryIcon,
  FileTextIcon,
  CalendarIcon,
  ServerIcon,
  RotateCcwIcon,
  UserIcon,
  PlusIcon,
  WorkflowIcon
} from "lucide-react";
import { cn } from "../../lib/utils";
import moment from "moment";
import RequestAnalyticsChart from "../shared/RequestAnalyticsChart";
import FaultAnalyticsChart from "../shared/FaultAnalyticsChart";
import WorkflowRunDetailModal, { formatWorkflowDuration } from "../shared/WorkflowRunDetailModal";
import { QuickPeriod } from "../shared/AnalyticsDateFilter";
import AgentsTable, { FilterConfig } from "../Forge/AgentsTable";

interface SortConfig {
  key: keyof Session | null;
  direction: "asc" | "desc";
}

interface AgentWithApplication extends Agent {
  application_name?: string;
}

interface AgentSortConfig {
  key: keyof AgentWithApplication | null;
  direction: "asc" | "desc";
}

interface TabItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const ApplicationOverview: React.FC = () => {
  const { apiKey } = usePermissions();
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  
  const [application, setApplication] = useState<Partial<Application> | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sessionsLoading, setSessionsLoading] = useState<boolean>(true);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: "desc" });
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [sessionModalVisible, setSessionModalVisible] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [sessionsByPage, setSessionsByPage] = useState<Record<number, Session[]>>({});
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [originalConfig, setOriginalConfig] = useState<Config>();
  const [intents, setIntents] = useState<ApplicationAction[]>([]);
  const [allIntents, setAllIntents] = useState<ApplicationAction[]>([]);
  const [intentsLoading, setIntentsLoading] = useState<boolean>(false);
  const [models, setModels] = useState<Model[]>([]);
  const [modelsLoading, setModelsLoading] = useState<boolean>(false);
  const [intentSortConfig, setIntentSortConfig] = useState<{ key: keyof ApplicationAction | null; direction: "asc" | "desc" }>({ key: null, direction: "desc" });
  const [modelSortConfig, setModelSortConfig] = useState<{ key: keyof Model | null; direction: "asc" | "desc" }>({ key: null, direction: "desc" });
  
  // Add pagination state for intents and models
  const [intentCurrentPage, setIntentCurrentPage] = useState(1);
  const [modelCurrentPage, setModelCurrentPage] = useState(1);
  const [totalIntents, setTotalIntents] = useState(0);
  const [totalModels, setTotalModels] = useState(0);
  
  const [modelsByPage, setModelsByPage] = useState<Record<number, Model[]>>({});
  
  // Add agent-related state
  const [agents, setAgents] = useState<AgentWithApplication[]>([]);
  const [allAgents, setAllAgents] = useState<AgentWithApplication[]>([]);
  const [agentsLoading, setAgentsLoading] = useState<boolean>(false);
  const [agentCurrentPage, setAgentCurrentPage] = useState(1);
  const [totalAgents, setTotalAgents] = useState(0);
  const [agentSortConfig, setAgentSortConfig] = useState<AgentSortConfig>({ key: null, direction: "desc" });
  const [agentDeleteDialogOpen, setAgentDeleteDialogOpen] = useState<number | null>(null);
  const [agentsLoaded, setAgentsLoaded] = useState<boolean>(false);
  const [agentFilterConfig, setAgentFilterConfig] = useState<FilterConfig>({
    applicationId: null,
    includeSubagents: false,
  });

  const [workflowRuns, setWorkflowRuns] = useState<WorkflowRun[]>([]);
  const [workflowRunsLoading, setWorkflowRunsLoading] = useState<boolean>(false);
  const [workflowRunsPage, setWorkflowRunsPage] = useState(1);
  const [totalWorkflowRuns, setTotalWorkflowRuns] = useState(0);
  const [selectedWorkflowRun, setSelectedWorkflowRun] = useState<WorkflowRun | null>(null);
  const [workflowRunDetailModalVisible, setWorkflowRunDetailModalVisible] = useState(false);
  
  const pageSize = 10;
  
  // Configuration save state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const configPanelRef = useRef<ConfigurationPanelRef>(null);

  // Analytics state
  const [requestAnalyticsData, setRequestAnalyticsData] = useState<RequestAnalyticsData[]>([]);
  const [requestAnalyticsLoading, setRequestAnalyticsLoading] = useState<boolean>(false);
  const [requestAnalyticsPeriod, setRequestAnalyticsPeriod] = useState<QuickPeriod>({ label: "1W", days: 7 });
  const [requestAnalyticsPeriodDays, setRequestAnalyticsPeriodDays] = useState<number>(7);

  const [faultAnalyticsData, setFaultAnalyticsData] = useState<FaultRateAnalyticsData[]>([]);
  const [faultAnalyticsLoading, setFaultAnalyticsLoading] = useState<boolean>(false);
  const [faultAnalyticsPeriod, setFaultAnalyticsPeriod] = useState<QuickPeriod>({ label: "1W", days: 7 });
  const [faultAnalyticsPeriodDays, setFaultAnalyticsPeriodDays] = useState<number>(7);

  const updateConfig = (
    config: Partial<Config>,
    loadingHandler: (loading: boolean) => void | null,
    save: boolean = true
  ) => {
    let newConfig: Partial<Config> = application?.meta;
    if (!newConfig) {
      newConfig = config;
    }
    if (!application) {
      return;
    }
    const newApplication = {
      ...application,
      meta: {
        ...newConfig,
        ...config,
      },
    };
    setApplication(newApplication);
    if (!save) {
      return;
    }
    ApplicationService.setApplicationConfig(
      application.id,
      config,
      loadingHandler,
      apiKey
    ).subscribe(
      (updatedConfig: Config) => {
        setApplication((prevState) => {
          if (!prevState) return prevState;
          return {
            ...prevState,
            meta: updatedConfig,
          };
        });
      },
      (error: any) => {
        console.error(error);
      }
    );
  };

  const resetConfig = () => {
    if (originalConfig) {
      updateConfig(originalConfig, () => {});
    }
  };

  const handleSaveStateChange = (hasUnsaved: boolean, saving: boolean) => {
    setHasUnsavedChanges(hasUnsaved);
    setIsSaving(saving);
  };

  const handleSaveChanges = () => {
    if (configPanelRef.current) {
      configPanelRef.current.saveChanges();
    }
  };

  // Update total sessions from application metadata
  const updateTotalSessionsFromMetadata = useCallback(() => {
    if (application?.meta?.metadata?.session_count !== undefined) {
      setTotalSessions(Number(application.meta.metadata.session_count) || 0);
    }
  }, [application]);

  useEffect(() => {
    if (applicationId) {
      ApplicationService.getApplication(
        Number(applicationId),
        setIsLoading,
        apiKey
      ).subscribe(
        (app: Application) => {
          setApplication(app);
          setOriginalConfig(app.meta);

          // Set total sessions from metadata
          if (app.meta?.metadata?.session_count !== undefined) {
            setTotalSessions(Number(app.meta.metadata.session_count) || 0);
          }
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [applicationId]);

  // Update total sessions when application metadata changes
  useEffect(() => {
    updateTotalSessionsFromMetadata();
  }, [updateTotalSessionsFromMetadata]);

  const loadSessions = useCallback(() => {
    if (!applicationId || activeTab !== "sessions") return;

    if (!sessionsByPage[currentPage]) {
      setSessionsLoading(true);
      SessionService.getApplicationSessions(
        applicationId,
        (currentPage - 1) * pageSize,
        pageSize,
        setSessionsLoading,
        apiKey
      ).subscribe(
        ({ sessions, total }: { sessions: Session[]; total: number }) => {
          const processedSessions = sessions.map((session) => ({
            ...session,
            timeline: session.timeline || [],
            cost: session.cost ?? -1,
          }));

          setSessions(processedSessions);
          // Update total sessions from API response as a fallback
          if (
            total &&
            (!application?.meta?.metadata?.session_count ||
              total > Number(application.meta.metadata.session_count))
          ) {
            setTotalSessions(total);
          }
          setSessionsByPage((prev) => ({
            ...prev,
            [currentPage]: processedSessions,
          }));
        },
        (error: any) => {
          console.error(error);
        }
      );
    } else {
      setSessions(sessionsByPage[currentPage]);
    }
  }, [
    applicationId,
    currentPage,
    pageSize,
    apiKey,
    sessionsByPage,
    activeTab,
    application,
  ]);

  useEffect(() => {
    if (applicationId) {
      loadSessions();
    }
  }, [loadSessions, refreshTrigger, activeTab]);

  const handleSort = (key: keyof Session) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedSessions = React.useMemo(() => {
    const sortableSessions = [...sessions];
    if (sortConfig.key) {
      sortableSessions.sort((a, b) => {
        const aValue = a[sortConfig.key!];
        const bValue = b[sortConfig.key!];
        
        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    } else {
      // Default sort by date_created descending
      sortableSessions.sort((a, b) => {
        const dateA = a.date_created > 0 ? a.date_created : 0;
        const dateB = b.date_created > 0 ? b.date_created : 0;
        return dateB - dateA;
      });
    }
    return sortableSessions;
  }, [sessions, sortConfig]);

  const handleSessionClick = (session: Session) => {
    setSelectedSessionId(session.session_id);
    setSessionModalVisible(true);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleSessionModalClose = () => {
    setSelectedSessionId(null);
    setSessionModalVisible(false);
  };

  const formatDate = (timestamp: number) => {
    if (timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const loadIntents = useCallback(() => {
    if (!applicationId || activeTab !== "intents") return;

    setIntentsLoading(true);
    ApplicationService.getApplicationActions(
      Number(applicationId),
      setIntentsLoading,
      apiKey
    ).subscribe(
      (actions: ApplicationAction[]) => {
        const sortedActions = actions.sort((a, b) => {
          const dateA = a.last_activity > 0 ? a.last_activity : 0;
          const dateB = b.last_activity > 0 ? b.last_activity : 0;
          return dateB - dateA;
        });

        setAllIntents(sortedActions);
        setTotalIntents(sortedActions.length);
      },
      (error: any) => {
        console.error(error);
        setIntentsLoading(false);
      }
    );
  }, [applicationId, activeTab, apiKey]);

  // Load models
  const loadModels = useCallback(() => {
    if (!applicationId || activeTab !== "models") return;

    if (!modelsByPage[modelCurrentPage]) {
      setModelsLoading(true);
      ModelService.getModelsByApplication(
        Number(applicationId),
        setModelsLoading,
        apiKey
      ).subscribe(
        (data: Model[]) => {
          // Filter to only include finetuned models
          const finetunedModels = data.filter((model) => model.finetuned);
          
          setTotalModels(finetunedModels.length);
          
          // Paginate the results
          const startIndex = (modelCurrentPage - 1) * pageSize;
          const endIndex = startIndex + pageSize;
          const paginatedModels = finetunedModels.slice(startIndex, endIndex);
          
          setModels(paginatedModels);
          setModelsByPage((prev) => ({
            ...prev,
            [modelCurrentPage]: paginatedModels,
          }));
        },
        (error: any) => {
          console.error("Error fetching models:", error);
          setModelsLoading(false);
          setModels([]);
        }
      );
    } else {
      setModels(modelsByPage[modelCurrentPage]);
    }
  }, [applicationId, activeTab, apiKey, modelCurrentPage, modelsByPage, pageSize]);

  // Sort and paginate agents based on current sort config and page
  const sortedAndPaginatedAgents = React.useMemo(() => {
    if (!allAgents.length) return [];
    
    // First, sort all agents
    const sortableAgents = [...allAgents];
    if (agentSortConfig.key) {
      sortableAgents.sort((a, b) => {
        const aValue = a[agentSortConfig.key!];
        const bValue = b[agentSortConfig.key!];
        
        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return agentSortConfig.direction === "asc" ? 1 : -1;
        if (bValue == null) return agentSortConfig.direction === "asc" ? -1 : 1;
        
        if (aValue < bValue) {
          return agentSortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return agentSortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    } else {
      // Default sort by date_created descending
      sortableAgents.sort((a, b) => {
        const dateA = a.date_created && a.date_created > 0 ? a.date_created : 0;
        const dateB = b.date_created && b.date_created > 0 ? b.date_created : 0;
        return dateB - dateA;
      });
    }
    
    // Then paginate the sorted results
    const startIndex = (agentCurrentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortableAgents.slice(startIndex, endIndex);
  }, [allAgents, agentSortConfig, agentCurrentPage, pageSize]);

  // Load agents
  const loadAgents = useCallback(() => {
    if (!applicationId || activeTab !== "agents") return;

    setAgentsLoading(true);
    // Use company-level endpoint with includeSubagents support, then filter for this application
    AgentService.getAgents(
      setAgentsLoading,
      apiKey,
      agentFilterConfig.includeSubagents
    ).subscribe(
      (response: any) => {
        // Handle both direct array and wrapped response formats
        const agentsList = Array.isArray(response) ? response : (response.agents || []);
        
        // Filter agents for this specific application and add application_name
        const filteredAgents = agentsList
          .filter((agent: Agent) => agent.application_id === Number(applicationId))
          .map((agent: Agent) => ({
            ...agent,
            application_name: application?.application_name
          }));
        
        setAllAgents(filteredAgents);
        setTotalAgents(filteredAgents.length);
        setAgentsLoaded(true);
      },
      (error: any) => {
        console.error("Error fetching agents:", error);
        setAgentsLoading(false);
        setAllAgents([]);
        setAgents([]);
        setTotalAgents(0);
      }
    );
  }, [applicationId, activeTab, apiKey, agentFilterConfig.includeSubagents, application?.application_name]);

  // Update displayed agents when sorting or pagination changes
  React.useEffect(() => {
    setAgents(sortedAndPaginatedAgents);
  }, [sortedAndPaginatedAgents]);

  const loadWorkflowRuns = useCallback(() => {
    if (!applicationId || activeTab !== "workflow-runs") return;

    setWorkflowRunsLoading(true);
    WorkflowService.getWorkflowRuns(
      Number(applicationId),
      (workflowRunsPage - 1) * pageSize,
      pageSize,
      setWorkflowRunsLoading,
      apiKey
    ).subscribe(
      (runs: WorkflowRun[]) => {
        setWorkflowRuns(runs);
        setTotalWorkflowRuns(
          runs.length === pageSize
            ? workflowRunsPage * pageSize + 1
            : (workflowRunsPage - 1) * pageSize + runs.length
        );
      },
      (error: unknown) => {
        console.error(error);
        setWorkflowRuns([]);
        setTotalWorkflowRuns(0);
      }
    );
  }, [applicationId, activeTab, apiKey, workflowRunsPage, pageSize]);

  useEffect(() => {
    if (activeTab === "workflow-runs") {
      loadWorkflowRuns();
    }
  }, [activeTab, workflowRunsPage, loadWorkflowRuns]);

  const handleWorkflowRunClick = (run: WorkflowRun) => {
    setSelectedWorkflowRun(run);
    setWorkflowRunDetailModalVisible(true);
  };

  const handleWorkflowRunModalClose = () => {
    setSelectedWorkflowRun(null);
    setWorkflowRunDetailModalVisible(false);
  };

  useEffect(() => {
    if (activeTab === "intents") {
      setIntentsLoading(true);
      setIntentCurrentPage(1);
      loadIntents();
    } else if (activeTab === "models") {
      setModelsLoading(true);
      setModelCurrentPage(1);
      loadModels();
    } else if (activeTab === "agents") {
      setAgentsLoading(true);
      setAgentCurrentPage(1);
      loadAgents();
    } else if (activeTab === "workflow-runs") {
      setWorkflowRunsLoading(true);
      setWorkflowRunsPage(1);
    }
  }, [activeTab]);

  // Reload agents when filter config changes
  useEffect(() => {
    if (activeTab === "agents" && agentsLoaded) {
      // Reset to first page and loaded state to force reload with new filters
      setAgentCurrentPage(1);
      setAgentsLoaded(false);
      loadAgents();
    }
  }, [agentFilterConfig.includeSubagents, activeTab, loadAgents]);

  // Reset agents data when application changes
  useEffect(() => {
    setAgentsLoaded(false);
    setAllAgents([]);
    setAgents([]);
    setTotalAgents(0);
  }, [applicationId]);

  // Load models when page changes
  useEffect(() => {
    if (activeTab === "models") {
      loadModels();
    }
  }, [modelCurrentPage, loadModels, activeTab]);

  // Agent CRUD handlers
  const handleAgentDelete = (agentId: number) => {
    if (!apiKey) return;
    
    setAgentsLoading(true);
    AgentService.deleteAgent(agentId, setAgentsLoading, apiKey).subscribe(
      () => {
        // Update both the full list and displayed agents
        setAllAgents((prevAgents) =>
          prevAgents.filter((agent) => agent.id !== agentId)
        );
        setAgents((prevAgents) =>
          prevAgents.filter((agent) => agent.id !== agentId)
        );
        setTotalAgents((prevTotal) => prevTotal - 1);
        setAgentDeleteDialogOpen(null);
        setAgentsLoading(false);
      },
      (error: any) => {
        console.error("Error deleting agent:", error);
        setAgentsLoading(false);
      }
    );
  };

  const handleAgentClone = (agentId: number, newAgentName: string, newApplicationName: string) => {
    if (!apiKey) return;
    
    setAgentsLoading(true);
    AgentService.cloneAgent(agentId, newAgentName, newApplicationName, setAgentsLoading, apiKey).subscribe(
      (response: any) => {
        // Refresh the agent list to include the newly cloned agent
        if (application?.id) {
          AgentService.getAgentsByApplication(application.id, setAgentsLoading, apiKey).subscribe(
            (agentsResponse: any) => {
              const fetchedAgents = Array.isArray(agentsResponse) ? agentsResponse : (agentsResponse.agents || []);
              setAllAgents(fetchedAgents);
              setAgentsLoading(false);
            },
            (error: any) => {
              console.error("Error refreshing agents after clone:", error);
              setAgentsLoading(false);
            }
          );
        }
      },
      (error: any) => {
        console.error("Error cloning agent:", error);
        setAgentsLoading(false);
      }
    );
  };

  const handleClearAgentFilters = () => {
    setAgentFilterConfig({
      applicationId: null,
      includeSubagents: false,
    });
    setAgentCurrentPage(1); // Reset to first page when clearing filters
    // Reset loaded state to force reload with cleared filters
    if (activeTab === "agents") {
      setAgentsLoaded(false);
    }
  };

  const handleCreateAgent = () => {
    navigate("/agent/new");
  };

  const handleIntentSort = (key: keyof ApplicationAction) => {
    let direction: "asc" | "desc" = "asc";
    if (intentSortConfig.key === key && intentSortConfig.direction === "asc") {
      direction = "desc";
    }
    setIntentSortConfig({ key, direction });
    setIntentCurrentPage(1);
  };

  const handleModelSort = (key: keyof Model) => {
    let direction: "asc" | "desc" = "asc";
    if (modelSortConfig.key === key && modelSortConfig.direction === "asc") {
      direction = "desc";
    }
    setModelSortConfig({ key, direction });
  };

  const sortedAndPaginatedIntents = React.useMemo(() => {
    if (!allIntents.length) return [];

    const sortableIntents = [...allIntents];
    if (intentSortConfig.key) {
      sortableIntents.sort((a, b) => {
        const aValue = a[intentSortConfig.key!];
        const bValue = b[intentSortConfig.key!];

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return intentSortConfig.direction === "asc" ? 1 : -1;
        if (bValue == null) return intentSortConfig.direction === "asc" ? -1 : 1;

        if (aValue < bValue) {
          return intentSortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return intentSortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    const startIndex = (intentCurrentPage - 1) * pageSize;
    return sortableIntents.slice(startIndex, startIndex + pageSize);
  }, [allIntents, intentSortConfig, intentCurrentPage, pageSize]);

  React.useEffect(() => {
    setIntents(sortedAndPaginatedIntents);
  }, [sortedAndPaginatedIntents]);

  const sortedModels = React.useMemo(() => {
    const sortableModels = [...models];
    if (modelSortConfig.key) {
      sortableModels.sort((a, b) => {
        const aValue = a[modelSortConfig.key!];
        const bValue = b[modelSortConfig.key!];
        
        if (aValue < bValue) {
          return modelSortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return modelSortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableModels;
  }, [models, modelSortConfig]);

  const getSessionCount = () => {
    if (application?.meta?.metadata?.session_count !== undefined) {
      return Number(application.meta.metadata.session_count) || 0;
    }
    return totalSessions;
  };

  // Analytics fetch functions
  const fetchRequestAnalytics = useCallback((period: QuickPeriod) => {
    if (!applicationId) return;

    setRequestAnalyticsLoading(true);
    
    const now = moment();
    const days = period.days;
    
    let startDate: string;
    let endDate: string;
    
    if (days === 1) {
      // For 1D period, get true 24 hours: from 24 hours ago to now (in UTC)
      startDate = now.clone().subtract(24, "hours").utc().format("YYYY-MM-DD HH:mm:ss");
      endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
    } else {
      // For multi-day periods, use the same logic as 1D but with more hours
      const totalHours = days * 24;
      startDate = now.clone().subtract(totalHours, "hours").utc().format("YYYY-MM-DD HH:mm:ss");
      endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
    }
    
    console.log(`📅 Request analytics period: ${days} days, ${startDate} to ${endDate}`);
    setRequestAnalyticsPeriodDays(days);

    AnalyticsService.getApplicationRequestAnalytics(
      Number(applicationId),
      setRequestAnalyticsLoading,
      apiKey,
      startDate,
      endDate
    ).subscribe(
      (response: ApplicationAnalyticsResponse) => {
        console.log("📊 Request analytics response:", response);
        setRequestAnalyticsData(response.data || []);
      },
      (error: any) => {
        console.error("Error fetching request analytics:", error);
        setRequestAnalyticsData([]);
      }
    );
  }, [applicationId, apiKey]);

  const fetchFaultAnalytics = useCallback((period: QuickPeriod) => {
    if (!applicationId) return;

    setFaultAnalyticsLoading(true);
    
    const now = moment();
    const days = period.days;
    
    let startDate: string;
    let endDate: string;
    
    if (days === 1) {
      // For 1D period, get true 24 hours: from 24 hours ago to now (in UTC)
      startDate = now.clone().subtract(24, "hours").utc().format("YYYY-MM-DD HH:mm:ss");
      endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
    } else {
      // For multi-day periods, use the same logic as 1D but with more hours
      const totalHours = days * 24;
      startDate = now.clone().subtract(totalHours, "hours").utc().format("YYYY-MM-DD HH:mm:ss");
      endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
    }
    
    console.log(`📅 Fault analytics period: ${days} days, ${startDate} to ${endDate}`);
    setFaultAnalyticsPeriodDays(days);

    AnalyticsService.getApplicationFaultRateAnalytics(
      Number(applicationId),
      setFaultAnalyticsLoading,
      apiKey,
      startDate,
      endDate
    ).subscribe(
      (response: FaultRateAnalyticsResponse) => {
        console.log("📊 Fault analytics response:", response);
        setFaultAnalyticsData(response.data || []);
      },
      (error: any) => {
        console.error("Error fetching fault analytics:", error);
        setFaultAnalyticsData([]);
      }
    );
  }, [applicationId, apiKey]);

  const handleRequestAnalyticsPeriodSelect = (period: QuickPeriod) => {
    setRequestAnalyticsPeriod(period);
    fetchRequestAnalytics(period);
  };

  const handleFaultAnalyticsPeriodSelect = (period: QuickPeriod) => {
    setFaultAnalyticsPeriod(period);
    fetchFaultAnalytics(period);
  };

  // Load request analytics when overview tab becomes active or period changes
  useEffect(() => {
    if (activeTab === "overview") {
      fetchRequestAnalytics(requestAnalyticsPeriod);
    }
  }, [activeTab, requestAnalyticsPeriod, fetchRequestAnalytics]);

  // Load fault analytics when overview tab becomes active or period changes
  useEffect(() => {
    if (activeTab === "overview") {
      fetchFaultAnalytics(faultAnalyticsPeriod);
    }
  }, [activeTab, faultAnalyticsPeriod, fetchFaultAnalytics]);

  // Load intents count for overview tab
  useEffect(() => {
    if (activeTab === "overview" && applicationId) {
      setIntentsLoading(true);
      ApplicationService.getApplicationActions(
        Number(applicationId),
        setIntentsLoading,
        apiKey
      ).subscribe(
        (actions: ApplicationAction[]) => {
          setTotalIntents(actions.length);
        },
        (error: any) => {
          console.error("Error fetching intents count for overview:", error);
          setTotalIntents(0);
        }
      );
    }
  }, [activeTab, applicationId, apiKey]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Smart pagination component
  const SmartPagination: React.FC<{
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    itemName: string;
    totalItems: number;
    itemsPerPage: number;
  }> = ({ currentPage, totalPages, onPageChange, itemName, totalItems, itemsPerPage }) => {
    const getVisiblePages = () => {
      const delta = 2; // Number of pages to show around current page
      const pages = [];
      
      if (totalPages <= 7) {
        // Show all pages if 7 or fewer
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Always show first page
        pages.push(1);
        
        if (currentPage > delta + 2) {
          pages.push("ellipsis-start");
        }
        
        // Show pages around current page
        const start = Math.max(2, currentPage - delta);
        const end = Math.min(totalPages - 1, currentPage + delta);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        if (currentPage < totalPages - delta - 1) {
          pages.push("ellipsis-end");
        }
        
        // Always show last page
        if (totalPages > 1) {
          pages.push(totalPages);
        }
      }
      
      return pages;
    };

    const visiblePages = getVisiblePages();

    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between px-6 py-4 border-t border-border">
        <div className="text-sm text-muted-foreground">
          Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} {itemName}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="gap-1"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            Previous
          </Button>
          <div className="flex items-center gap-1">
            {visiblePages.map((page, index) => {
              if (typeof page === "string") {
                return (
                  <span
                    key={`${page}-${index}`}
                    className="px-2 py-1 text-sm text-muted-foreground"
                  >
                    ...
                  </span>
                );
              }
              
              const isCurrentPage = page === currentPage;
              return (
                <Button
                  key={page}
                  variant={isCurrentPage ? "default" : "ghost"}
                  size="sm"
                  onClick={() => onPageChange(page)}
                  className={cn(
                    "w-8 h-8 p-0",
                    isCurrentPage && "bg-maitai-green text-white hover:bg-maitai-green/90"
                  )}
                >
                  {page}
                </Button>
              );
            })}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="gap-1"
          >
            Next
            <ChevronRightIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  const SortableHeader: React.FC<{ 
    children: React.ReactNode; 
    sortKey: keyof Session;
    className?: string;
  }> = ({ children, sortKey, className }) => (
    <TableHead className={cn("cursor-pointer hover:bg-accent/20 transition-colors", className)}>
      <button
        onClick={() => handleSort(sortKey)}
        className={cn(
          "flex items-center gap-2 font-gc-med text-muted-foreground hover:text-foreground transition-colors",
          sortConfig.key === sortKey && "text-maitai-green"
        )}
      >
        {children}
        <ArrowUpDownIcon className={cn(
          "w-3 h-3 transition-colors",
          sortConfig.key === sortKey && "text-maitai-green"
        )} />
      </button>
    </TableHead>
  );

  const IntentSortableHeader: React.FC<{ 
    children: React.ReactNode; 
    sortKey: keyof ApplicationAction;
    className?: string;
  }> = ({ children, sortKey, className }) => (
    <TableHead className={cn("cursor-pointer hover:bg-accent/20 transition-colors", className)}>
      <button
        onClick={() => handleIntentSort(sortKey)}
        className={cn(
          "flex items-center gap-2 font-gc-med text-muted-foreground hover:text-foreground transition-colors",
          intentSortConfig.key === sortKey && "text-maitai-green"
        )}
      >
        {children}
        <ArrowUpDownIcon className={cn(
          "w-3 h-3 transition-colors",
          intentSortConfig.key === sortKey && "text-maitai-green"
        )} />
      </button>
    </TableHead>
  );

  const ModelSortableHeader: React.FC<{ 
    children: React.ReactNode; 
    sortKey: keyof Model;
    className?: string;
  }> = ({ children, sortKey, className }) => (
    <TableHead className={cn("cursor-pointer hover:bg-accent/20 transition-colors", className)}>
      <button
        onClick={() => handleModelSort(sortKey)}
        className={cn(
          "flex items-center gap-2 font-gc-med text-muted-foreground hover:text-foreground transition-colors",
          modelSortConfig.key === sortKey && "text-maitai-green"
        )}
      >
        {children}
        <ArrowUpDownIcon className={cn(
          "w-3 h-3 transition-colors",
          modelSortConfig.key === sortKey && "text-maitai-green"
        )} />
      </button>
    </TableHead>
  );

  // Tab configuration
  const tabs: TabItem[] = [
    {
      key: "overview",
      label: "Overview",
      icon: <AppWindowIcon className="w-4 h-4" />,
      content: (
                 <div className="space-y-8">
           {/* Key Metrics */}
           <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
             <Card className="border-border">
               <CardContent className="p-6">
                 <div className="space-y-2">
                   <div className="flex items-center gap-2">
                     <FileTextIcon className="w-4 h-4 text-muted-foreground" />
                     <p className="text-sm text-muted-foreground font-gc-reg">Total Sessions</p>
                   </div>
                   <p className="text-lg font-gc-med text-foreground">{getSessionCount()}</p>
                 </div>
               </CardContent>
             </Card>
             
             <Card className="border-border">
               <CardContent className="p-6">
                 <div className="space-y-2">
                   <div className="flex items-center gap-2">
                     <AppWindowIcon className="w-4 h-4 text-muted-foreground" />
                     <p className="text-sm text-muted-foreground font-gc-reg">Intents</p>
                   </div>
                   <p className="text-lg font-gc-med text-foreground">
                     {totalIntents}
                   </p>
                 </div>
               </CardContent>
             </Card>
             
                         <Card className="border-border">
              <CardContent className="p-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground font-gc-reg">Last Activity</p>
                  </div>
                  <p className="text-lg font-gc-med text-foreground">
                    {application?.last_activity && application.last_activity > 0 
                      ? moment(application.last_activity).fromNow()
                      : "Never"
                    }
                  </p>
                </div>
              </CardContent>
            </Card>
             
             <Card className="border-border">
               <CardContent className="p-6">
                 <div className="space-y-2">
                   <div className="flex items-center gap-2">
                     <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                     <p className="text-sm text-muted-foreground font-gc-reg">Created</p>
                   </div>
                   <p className="text-lg font-gc-med text-foreground">
                     {moment(application?.date_created).format("MMM D, YYYY")}
                   </p>
                 </div>
               </CardContent>
             </Card>
          </div>

          {/* Analytics Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RequestAnalyticsChart
              data={requestAnalyticsData}
              loading={requestAnalyticsLoading}
              title="Request Activity"
              onQuickPeriodSelect={handleRequestAnalyticsPeriodSelect}
              selectedPeriod={requestAnalyticsPeriod}
              periodDays={requestAnalyticsPeriodDays}
            />
            <FaultAnalyticsChart
              data={faultAnalyticsData}
              loading={faultAnalyticsLoading}
              title="Fault Rate"
              onQuickPeriodSelect={handleFaultAnalyticsPeriodSelect}
              selectedPeriod={faultAnalyticsPeriod}
              periodDays={faultAnalyticsPeriodDays}
            />
          </div>
        </div>
      )
    },
    {
      key: "agents",
      label: "Agents",
      icon: <UserIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">Agents</h3>
              <p className="text-sm text-muted-foreground">Manage application agents and their configurations</p>
            </div>
            <Button
              onClick={handleCreateAgent}
              className="gap-2"
            >
              <PlusIcon className="w-4 h-4" />
              Create Agent
            </Button>
          </div>
          <AgentsTable 
            agents={agents}
            isLoading={agentsLoading}
            currentPage={agentCurrentPage}
            setCurrentPage={setAgentCurrentPage}
            sortConfig={agentSortConfig}
            setSortConfig={setAgentSortConfig}
            onDelete={handleAgentDelete}
            onClone={handleAgentClone}
            onCreateAgent={handleCreateAgent}
            deleteDialogOpen={agentDeleteDialogOpen}
            setDeleteDialogOpen={setAgentDeleteDialogOpen}
            pageSize={pageSize}
            totalAgents={totalAgents}
            applications={application ? [application] : []}
            filterConfig={agentFilterConfig}
            setFilterConfig={setAgentFilterConfig}
            onClearFilters={handleClearAgentFilters}
          />
        </div>
      )
    },
    {
      key: "intents",
      label: "Intents",
      icon: <FileTextIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-gc-med text-foreground">Intents</h3>
            <p className="text-sm text-muted-foreground">Manage application intents and their configurations</p>
          </div>
                     {intentsLoading && intents.length === 0 ? (
             <div className="space-y-4">
               {[...Array(5)].map((_, i) => (
                 <div key={i} className="flex items-center gap-4 py-4">
                   <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                   <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                   <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                   <div className="h-4 bg-muted rounded w-16 animate-pulse" />
                   <div className="h-4 bg-muted rounded w-20 animate-pulse" />
                 </div>
               ))}
             </div>
           ) : intents.length === 0 ? (
             <div className="flex flex-col items-center justify-center py-24 text-center">
               <div className="space-y-3">
                 <h3 className="text-lg font-gc-med text-foreground">
                   No intents found
                 </h3>
                 <p className="text-muted-foreground max-w-md">
                   Create your first intent to get started.
                 </p>
               </div>
             </div>
           ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-border hover:bg-transparent">
                    <IntentSortableHeader sortKey="action_type" className="w-[35%]">
                      Intent Name
                    </IntentSortableHeader>
                    <IntentSortableHeader sortKey="date_created" className="w-[20%]">
                      Date Created
                    </IntentSortableHeader>
                    <IntentSortableHeader sortKey="last_activity" className="w-[20%]">
                      Last Activity
                    </IntentSortableHeader>
                    <TableHead className="w-[15%]">Requests</TableHead>
                    <TableHead className="w-[10%]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {intents.map((intent) => (
                    <TableRow
                      key={intent.id}
                      className="cursor-pointer hover:bg-accent/20 transition-colors border-b border-border/50"
                      onClick={() => navigate(`/application/${intent.application_id}/intent/${intent.id}`)}
                    >
                      <TableCell className="py-4">
                        <div className="text-sm font-gc-reg text-foreground hover:text-maitai-green transition-colors">
                          {intent.action_type}
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="space-y-1">
                          <div className="text-sm font-gc-reg text-foreground">
                            {formatDate(intent.date_created)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatTime(intent.date_created)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="space-y-1">
                          <div className="text-sm font-gc-reg text-foreground">
                            {intent.last_activity > 0 ? formatDate(intent.last_activity) : "Never"}
                          </div>
                          {intent.last_activity > 0 && (
                            <div className="text-xs text-muted-foreground">
                              {formatTime(intent.last_activity)}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <span className="text-sm font-gc-med text-foreground">
                          {intent.request_count || 0}
                        </span>
                      </TableCell>
                      <TableCell className="py-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/application/${intent.application_id}/intent/${intent.id}`);
                          }}
                        >
                          <EyeIcon className="w-3 h-3" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination for Intents */}
              <SmartPagination
                currentPage={intentCurrentPage}
                totalPages={Math.ceil(totalIntents / pageSize)}
                onPageChange={setIntentCurrentPage}
                itemName="intents"
                totalItems={totalIntents}
                itemsPerPage={pageSize}
              />
            </>
          )}
        </div>
      )
    },
    {
      key: "sessions",
      label: "Sessions",
      icon: <HistoryIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">Sessions</h3>
              <p className="text-sm text-muted-foreground">View and manage application sessions</p>
            </div>
            <Input
              placeholder="Search sessions..."
              className="w-80"
            />
          </div>
          
                     {sessionsLoading ? (
             <div className="space-y-4">
               {[...Array(5)].map((_, i) => (
                 <div key={i} className="flex items-center gap-4 py-4">
                   <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                   <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                   <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                   <div className="h-4 bg-muted rounded w-16 animate-pulse" />
                   <div className="h-4 bg-muted rounded w-16 animate-pulse" />
                   <div className="h-4 bg-muted rounded w-16 animate-pulse" />
                   <div className="h-4 bg-muted rounded w-20 animate-pulse" />
                 </div>
               ))}
             </div>
           ) : sortedSessions.length === 0 ? (
             <div className="flex flex-col items-center justify-center py-24 text-center">
               <div className="space-y-3">
                 <h3 className="text-lg font-gc-med text-foreground">
                   No sessions found
                 </h3>
                 <p className="text-muted-foreground max-w-md">
                   No sessions have been created for this application yet.
                 </p>
               </div>
             </div>
           ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-border hover:bg-transparent">
                    <SortableHeader sortKey="date_created" className="w-[20%]">
                      Date Created
                    </SortableHeader>
                    <TableHead className="w-[25%]">Session ID</TableHead>
                    <SortableHeader sortKey="date_ended" className="w-[15%]">
                      Last Activity
                    </SortableHeader>
                    <TableHead className="w-[10%]">Score</TableHead>
                    <TableHead className="w-[10%]">Requests</TableHead>
                    <TableHead className="w-[10%]">Faults</TableHead>
                    <TableHead className="w-[10%]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedSessions.map((session) => (
                    <TableRow
                      key={session.session_id}
                      className="cursor-pointer hover:bg-accent/20 transition-colors border-b border-border/50"
                      onClick={() => handleSessionClick(session)}
                    >
                      <TableCell className="py-4">
                        <div className="space-y-1">
                          <div className="text-sm font-gc-reg text-foreground">
                            {formatDate(session.date_created)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatTime(session.date_created)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
                            {session.session_id?.length > 12
                              ? `${session.session_id.substring(0, 6)}...${session.session_id.substring(
                                  session.session_id.length - 6
                                )}`
                              : session.session_id}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(session.session_id || "");
                            }}
                          >
                            <CopyIcon className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="space-y-1">
                          <div className="text-sm font-gc-reg text-foreground">
                            {session.date_ended ? formatDate(session.date_ended) : formatDate(session.date_created)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {session.date_ended ? formatTime(session.date_ended) : formatTime(session.date_created)}
                          </div>
                        </div>
                      </TableCell>
                                             <TableCell className="py-4">
                         <div className={cn(
                           "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
                           getSessionScoreColor(session) === "success" && "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
                           getSessionScoreColor(session) === "error" && "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
                           getSessionScoreColor(session) === "warning" && "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
                           getSessionScoreColor(session) === "default" && "bg-muted text-foreground"
                         )}>
                           {calculateScore(session)}
                         </div>
                       </TableCell>
                      <TableCell className="py-4">
                        <span className="text-sm font-gc-med text-foreground">
                          {session.request_count !== undefined
                            ? session.request_count
                            : session.timeline.length}
                        </span>
                      </TableCell>
                      <TableCell className="py-4">
                        <span className="text-sm font-gc-med text-foreground">
                          {session.fault_count !== undefined
                            ? session.fault_count
                            : session.timeline.reduce((acc, turn) => {
                                const hasFault =
                                  turn.eval_request?.eval_results_set?.evaluation_results?.some(
                                    (evalResult: EvaluateResult) =>
                                      evalResult.status === "FAULT"
                                  );
                                return acc + (hasFault ? 1 : 0);
                              }, 0)}
                        </span>
                      </TableCell>
                      <TableCell className="py-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSessionClick(session);
                          }}
                        >
                          <EyeIcon className="w-3 h-3" />
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <SmartPagination
                currentPage={currentPage}
                totalPages={Math.ceil(getSessionCount() / pageSize)}
                onPageChange={setCurrentPage}
                itemName="sessions"
                totalItems={getSessionCount()}
                itemsPerPage={pageSize}
              />
            </>
          )}
        </div>
      )
    },
    {
      key: "workflow-runs",
      label: "Workflow Runs",
      icon: <WorkflowIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-gc-med text-foreground">Workflow Runs</h3>
            <p className="text-sm text-muted-foreground">View workflow invocations and their inputs, outputs, and execution profiles</p>
          </div>
          {workflowRunsLoading && workflowRuns.length === 0 ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 py-4">
                  <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                  <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                  <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                  <div className="h-4 bg-muted rounded w-16 animate-pulse" />
                  <div className="h-4 bg-muted rounded w-20 animate-pulse" />
                </div>
              ))}
            </div>
          ) : workflowRuns.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="space-y-3">
                <h3 className="text-lg font-gc-med text-foreground">
                  No workflow runs found
                </h3>
                <p className="text-muted-foreground max-w-md">
                  Workflow runs will appear here when workflows bound to this application are invoked.
                </p>
              </div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-border hover:bg-transparent">
                    <TableHead className="w-[18%] text-left">Date</TableHead>
                    <TableHead className="w-[22%] text-left">Workflow</TableHead>
                    <TableHead className="w-[20%] text-left">Session ID</TableHead>
                    <TableHead className="w-[10%] text-center">Status</TableHead>
                    <TableHead className="w-[10%] text-left">Duration</TableHead>
                    <TableHead className="w-[10%] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workflowRuns.map((run) => (
                    <TableRow
                      key={run.id}
                      className="cursor-pointer hover:bg-accent/20 transition-colors border-b border-border/50"
                      onClick={() => handleWorkflowRunClick(run)}
                    >
                      <TableCell className="py-4 text-left">
                        <div className="space-y-1">
                          <div className="text-sm font-gc-reg text-foreground">
                            {run.date_created ? moment(run.date_created).format("MMM D, YYYY") : "-"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {run.date_created ? moment(run.date_created).format("h:mm A") : ""}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4 text-left">
                        <span className="text-sm font-gc-reg text-foreground">
                          {run.workflow_name || run.workflow_id}
                        </span>
                      </TableCell>
                      <TableCell className="py-4 text-left">
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
                            {run.session_id?.length > 12
                              ? `${run.session_id.substring(0, 6)}...${run.session_id.substring(run.session_id.length - 6)}`
                              : run.session_id}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(run.session_id || "");
                            }}
                          >
                            <CopyIcon className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="py-4 text-center">
                        <div className={cn(
                          "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
                          run.status === "COMPLETED" && "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
                          run.status === "FAILED" && "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
                          run.status === "TIMEOUT" && "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
                          run.status === "RUNNING" && "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
                          (run.status !== "COMPLETED" && run.status !== "FAILED" && run.status !== "TIMEOUT" && run.status !== "RUNNING") && "bg-muted text-foreground"
                        )}>
                          {run.status}
                        </div>
                      </TableCell>
                      <TableCell className="py-4 text-left">
                        <span className="text-sm font-gc-reg text-foreground">
                          {formatWorkflowDuration(run.profile)}
                        </span>
                      </TableCell>
                      <TableCell className="py-4 text-right">
                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleWorkflowRunClick(run);
                            }}
                          >
                            <EyeIcon className="w-3 h-3" />
                            Details
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {totalWorkflowRuns > pageSize && (
                <SmartPagination
                  currentPage={workflowRunsPage}
                  totalPages={Math.ceil(totalWorkflowRuns / pageSize)}
                  onPageChange={setWorkflowRunsPage}
                  itemName="runs"
                  totalItems={totalWorkflowRuns}
                  itemsPerPage={pageSize}
                />
              )}
            </>
          )}
        </div>
      )
    },
    {
      key: "models",
       label: "Models",
       icon: <ServerIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-gc-med text-foreground">Models</h3>
            <p className="text-sm text-muted-foreground">Manage application models and configurations</p>
          </div>
                     {modelsLoading && models.length === 0 ? (
             <div className="space-y-4">
               {[...Array(5)].map((_, i) => (
                 <div key={i} className="flex items-center gap-4 py-4">
                   <div className="h-4 bg-muted rounded w-48 animate-pulse" />
                   <div className="h-4 bg-muted rounded w-32 animate-pulse" />
                   <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                   <div className="h-4 bg-muted rounded w-28 animate-pulse" />
                 </div>
               ))}
             </div>
           ) : sortedModels.length === 0 ? (
             <div className="flex flex-col items-center justify-center py-24 text-center">
               <div className="space-y-3">
                 <h3 className="text-lg font-gc-med text-foreground">
                   No fine-tuned models found
                 </h3>
                 <p className="text-muted-foreground max-w-md">
                   No fine-tuned models have been created yet.
                 </p>
               </div>
             </div>
           ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-border hover:bg-transparent">
                    <ModelSortableHeader sortKey="model" className="w-[35%]">
                      Model Name
                    </ModelSortableHeader>
                    <TableHead className="w-[25%]">Base Model</TableHead>
                    <TableHead className="w-[15%]">Status</TableHead>
                    <ModelSortableHeader sortKey="date_added" className="w-[25%]">
                      Date Created
                    </ModelSortableHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedModels.map((model) => (
                    <TableRow
                      key={model.id}
                      className="hover:bg-accent/20 transition-colors border-b border-border/50"
                    >
                      <TableCell className="py-4">
                        <div className="text-sm font-gc-reg text-foreground">
                          {model.model}
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <span className="text-sm font-gc-reg text-foreground">
                          {model.base_model || "-"}
                        </span>
                      </TableCell>
                      <TableCell className="py-4">
                        {model.status ? (
                          <div className={cn(
                            "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
                            model.status.toLowerCase() === "active" || model.status.toLowerCase() === "enabled" 
                              ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                              : model.status.toLowerCase() === "disabled"
                              ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
                              : model.status.toLowerCase() === "pending"
                              ? "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400"
                              : "bg-muted text-foreground"
                          )}>
                            {model.status.toUpperCase()}
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="py-4">
                        {model.date_added ? (
                          <div className="space-y-1">
                            <div className="text-sm font-gc-reg text-foreground">
                              {formatDate(model.date_added)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {formatTime(model.date_added)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination for Models */}
              <SmartPagination
                currentPage={modelCurrentPage}
                totalPages={Math.ceil(totalModels / pageSize)}
                onPageChange={setModelCurrentPage}
                itemName="models"
                totalItems={totalModels}
                itemsPerPage={pageSize}
              />
            </>
          )}
        </div>
      )
    },
    {
      key: "configuration",
      label: "Configuration",
      icon: <SettingsIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">Configuration</h3>
              <p className="text-sm text-muted-foreground">Manage application settings and parameters</p>
            </div>
            <div className="flex items-center gap-3">
              {hasUnsavedChanges && (
                <span className="text-sm text-muted-foreground">
                  You have unsaved changes
                </span>
              )}
              <Button
                variant="outline"
                onClick={resetConfig}
                className="gap-2"
              >
                <RotateCcwIcon className="w-4 h-4" />
                Reset to Default
              </Button>
              <Button
                onClick={handleSaveChanges}
                disabled={!hasUnsavedChanges || isSaving}
                className="gap-2"
              >
                {isSaving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                Save Changes
              </Button>
            </div>
          </div>
          <ConfigurationPanel
            ref={configPanelRef}
            config={application?.meta}
            updateConfig={updateConfig}
            resetConfig={resetConfig}
            showSystemPrompt={false}
            onSaveStateChange={handleSaveStateChange}
          />
        </div>
      )
    }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
          {/* Header Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="h-4 bg-muted rounded w-32 animate-pulse mb-6" />
              <div className="space-y-2">
                <div className="h-10 bg-muted rounded w-64 animate-pulse" />
                <div className="h-5 bg-muted rounded w-96 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          {/* Content Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-8 animate-pulse" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/applications")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Applications
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">{application?.application_name}</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                {application?.application_name}
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Manage intents, monitor sessions, and configure application settings
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Tab Navigation */}
            <div className="border-b border-border">
              <nav className="flex space-x-8 px-8 py-4">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                      activeTab === tab.key
                        ? "bg-maitai-green/10 text-maitai-green"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                    )}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-8">
              {tabs.find(tab => tab.key === activeTab)?.content}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Session Details Modal */}
      <Dialog open={sessionModalVisible} onOpenChange={setSessionModalVisible}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-background border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <HistoryIcon className="w-5 h-5 text-maitai-green" />
              Session Details
            </DialogTitle>
          </DialogHeader>
          {selectedSessionId && applicationId && (
            <SessionOverview
              sessionId={selectedSessionId}
              applicationId={applicationId}
              refreshTrigger={refreshTrigger}
            />
          )}
        </DialogContent>
      </Dialog>

      <WorkflowRunDetailModal
        run={selectedWorkflowRun}
        open={workflowRunDetailModalVisible}
        onOpenChange={(open) => {
          setWorkflowRunDetailModalVisible(open);
          if (!open) setSelectedWorkflowRun(null);
        }}
        onViewSession={
          applicationId && selectedWorkflowRun?.session_id
            ? () => {
                handleWorkflowRunModalClose();
                setSelectedSessionId(selectedWorkflowRun!.session_id);
                setSessionModalVisible(true);
              }
            : undefined
        }
      />

    </div>
  );
};

export default ApplicationOverview;
